/**
 * JoinByCode — code entry screen.
 *
 * Three inputs: WORD-WORD-NN. On submit, locally bootstrap a mock
 * lobby for development and navigate to /join/:sessionId. Once Stream
 * A's session-registry route is wired, this should POST the code,
 * receive a sessionId, and navigate.
 */

import { useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { fontStacks } from '@qd/design-tokens';
import { SESSION_CODE_REGEX } from '@qd/protocol';
import { SlateBackground } from '../components/base/SlateBackground';
import { ChalkButton } from '../components/base/ChalkButton';
import { ChalkText } from '../components/base/ChalkText';
import { useIsMobile } from '../hooks/useIsMobile';

export function JoinByCode() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [n, setN] = useState('');
  const inputBRef = useRef<HTMLInputElement>(null);
  const inputNRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  function handleSubmit(event?: FormEvent) {
    event?.preventDefault();
    if (a.length < 2 || b.length < 2 || n.length === 0) return;
    const code = `${a.toUpperCase()}-${b.toUpperCase()}-${n.padStart(2, '0')}`;
    if (!SESSION_CODE_REGEX.test(code)) return;
    navigate(`/game/${code}/lobby`);
  }

  return (
    <SlateBackground>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100dvh',
          padding: 24,
          gap: 40,
        }}
      >
        <ChalkText size="heading" underline>
          Enter Session Code
        </ChalkText>

        <form
          onSubmit={handleSubmit}
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 14 : 12,
            alignItems: 'center',
            flexWrap: isMobile ? 'nowrap' : 'wrap',
            justifyContent: 'center',
            width: '100%',
            maxWidth: 'min(420px, 92vw)',
          }}
        >
          <CodeInput
            value={a}
            onChange={setA}
            placeholder="WORD"
            maxLength={12}
            isMobile={isMobile}
            onEnter={() => inputBRef.current?.focus()}
            onPaste={(raw) => {
              // Paste full code e.g. "ALPHA-BRAVO-42" into the first box
              const parts = raw.toUpperCase().split('-');
              if (parts.length === 3) {
                const [pa, pb, pn] = parts;
                setA(pa ?? '');
                setB(pb ?? '');
                setN(pn ?? '');
                // Submit on next tick after state settles
                setTimeout(() => {
                  const code = `${pa}-${pb}-${(pn ?? '').padStart(2, '0')}`;
                  if (SESSION_CODE_REGEX.test(code)) navigate(`/game/${code}/lobby`);
                }, 50);
              }
            }}
          />
          {/* Dashes are decorative separators between word/number parts.
              On mobile the column layout already separates them visually,
              so we suppress the dashes to avoid an awkward floating "—". */}
          {!isMobile && <Dash />}
          <CodeInput
            value={b}
            onChange={setB}
            placeholder="WORD"
            maxLength={12}
            isMobile={isMobile}
            inputRef={inputBRef}
            onEnter={() => inputNRef.current?.focus()}
          />
          {!isMobile && <Dash />}
          <CodeInput
            value={n}
            onChange={(v) => setN(v.replace(/[^0-9]/g, ''))}
            placeholder="NN"
            maxLength={2}
            small
            isMobile={isMobile}
            inputRef={inputNRef}
            onEnter={() => handleSubmit()}
          />
          {/* Hidden submit so Enter inside any input triggers the form */}
          <button ref={submitRef} type="submit" style={{ display: 'none' }} aria-hidden />
        </form>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
          <ChalkButton size="lg" variant="primary" onClick={() => handleSubmit()}>
            Join
          </ChalkButton>
          <ChalkButton size="lg" variant="secondary" onClick={() => alert('Camera permission required to scan QR code.')}>
            Scan QR Code
          </ChalkButton>
        </div>

        <ChalkButton
          size="md"
          variant="secondary"
          onClick={() => navigate('/')}
          style={{ marginTop: 24, fontSize: 16 }}
        >
          ← Back to Main Menu
        </ChalkButton>
      </div>
    </SlateBackground>
  );
}

interface CodeInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  maxLength: number;
  small?: boolean;
  isMobile?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  onEnter?: () => void;
  onPaste?: (raw: string) => void;
}

function CodeInput({ value, onChange, placeholder, maxLength, small, isMobile, inputRef, onEnter, onPaste }: CodeInputProps) {
  // Mobile: stacked vertically, inputs fill the form's max width with a
  // smaller font so each field stays a comfortable single tap target
  // (>=44px) without overflowing 375px. The "small" (NN) field stays
  // narrower even on mobile to communicate its 2-digit nature.
  const mobileWidth = small ? 96 : '100%';
  const desktopWidth = small ? 90 : 180;
  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value.toUpperCase())}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onEnter?.();
        }
      }}
      onPaste={(e) => {
        if (onPaste) {
          const pasted = e.clipboardData.getData('text');
          if (pasted.includes('-')) {
            e.preventDefault();
            onPaste(pasted.trim());
          }
        }
      }}
      placeholder={placeholder}
      maxLength={maxLength}
      autoComplete="off"
      spellCheck={false}
      inputMode={small ? 'numeric' : 'text'}
      style={{
        background: 'rgba(255,255,255,0.12)',
        color: '#ffffff',
        fontFamily: fontStacks.mono,
        fontSize: isMobile ? (small ? 22 : 26) : small ? 28 : 34,
        letterSpacing: '0.18em',
        padding: isMobile ? '10px 14px' : '12px 18px',
        border: `2px solid rgba(255,255,255,0.35)`,
        borderRadius: 8,
        width: isMobile ? mobileWidth : desktopWidth,
        maxWidth: '100%',
        minHeight: 44,
        boxSizing: 'border-box',
        textAlign: 'center',
        textTransform: 'uppercase',
        outline: 'none',
        backdropFilter: 'blur(4px)',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = '#60a5fa';
        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(96,165,250,0.3)';
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    />
  );
}

function Dash() {
  return (
    <span
      style={{
        fontFamily: fontStacks.mono,
        fontSize: 'clamp(22px, 4vw, 32px)',
        color: '#ffffff',
        opacity: 0.5,
      }}
    >
      —
    </span>
  );
}
